import { computed } from 'vue';
import { useGlobalStore } from '../store/global'
import { useTokenStore } from "../store/token";

export default defineNuxtPlugin((nuxtApp) => {

  const tokenStore = useTokenStore();
  const globalStore = useGlobalStore();

  // Initialiser le store global
  globalStore.init();

  const isUserFromUS = computed(() => {
    try {
      const userCountryId = Number(globalStore.user?.country_id);
      const country = globalStore.countries?.data?.find(it => it.id == userCountryId);
      return country?.code_iso === 'US';
    } catch {
      return false;
    }
  });

  function slugify(text) {
    if (!text) return "";

    const slug = text
      .toString()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[^a-zA-Z0-9]/g, "-")
      .replace(/-+/g, "-")
      .replace(/^-|-$/g, "");

    return slug.toLowerCase();
  }

  /** BOLDUC dimensions are stored as "L 12 cm x W 34 cm x H 56 cm" or "Ø 12 cm x H 56 cm". */
  function convertDimensions(dimensions: string | null | undefined) {
    const raw = dimensions != null ? String(dimensions).trim() : '';
    if (!raw) return '';

    if (!isUserFromUS.value) return raw;
    if (!/\bcm\b/i.test(raw)) return raw;

    const converted = raw
      .split('x')
      .map((part) => {
        const trimmed = part.trim();
        if (!trimmed || trimmed.toLowerCase() === 'undefined') return '';

        return trimmed.replace(/(-?\d+(?:[.,]\d+)?)\s*cm\b/gi, (match, value) => {
          const cmValue = Number.parseFloat(value.replace(',', '.'));
          if (Number.isNaN(cmValue)) return match;
          return (cmValue * 0.393701).toFixed(2);
        });
      })
      .filter(Boolean)
      .join(' x ');

    return converted ? `${converted} in` : raw;
  }

  /** PIM stores e.g. "12 KILOGRAM"; display normalizes unit, US converts only when source is kg. */
  function convertWeight(weight: string | null | undefined) {
    const raw = weight != null ? String(weight).trim() : '';
    if (!raw) return '';

    const normalizedWeight = raw.replace(/\bKILOGRAM\b/gi, 'kg');

    if (isUserFromUS.value && /\bkg\b/i.test(normalizedWeight)) {
      const kgValue = parseFloat(normalizedWeight);
      if (Number.isNaN(kgValue)) return normalizedWeight;
      return `${(kgValue * 2.20462).toFixed(2)} lbs`;
    }

    return normalizedWeight;
  }

  function nl2br(str: string | undefined | null, is_xhtml?: boolean): string {
    if (typeof str === 'undefined' || str === null) {
      return '';
    }
    const breakTag = (is_xhtml || typeof is_xhtml === 'undefined') ? '<br />' : '<br>';

    // D'abord traiter les séquences combinées \r\n[br] pour éviter la duplication
    let result = (str + '').replace(/(\r\n|\n\r|\r|\n)\[br\]/g, breakTag);

    // Puis traiter les \r\n restants (qui ne sont pas suivis de [br])
    result = result.replace(/([^>\r\n]?)(\r\n|\n\r|\r|\n)/g, '$1' + breakTag + '$2');

    // Enfin traiter les [br] restants (qui ne sont pas précédés de \r\n)
    result = result.replace(/\[br\] /g, breakTag).replace(/\[br\]/g, breakTag);

    // Fusionner les multiples <br> consécutifs en un seul (avec espaces/retours possibles entre eux)
    result = result.replace(/(<br\s*\/?>[\s\r\n]*)+/gi, breakTag);

    return result;
  }

  // Version spécifique pour ProductHeader : remplace les virgules par <br>
  function nl2brCommas(str: string | undefined | null, is_xhtml?: boolean): string {
    if (typeof str === 'undefined' || str === null) {
      return '';
    }
    const breakTag = (is_xhtml || typeof is_xhtml === 'undefined') ? '<br />' : '<br>';
    return (str + '').replace(/, /g, breakTag + ' ');
  }

  const buildLanguageCandidates = (lang: string) => {
    const base = (lang || 'fr').trim();
    const lower = base.toLowerCase();
    const upper = base.toUpperCase();
    const candidates = new Set<string>();
    const push = (value?: string) => {
      if (value) {
        candidates.add(value);
      }
    };

    push(base);
    push(lower);
    push(upper);

    if (lower.startsWith('fr')) {
      ['fr_FR', 'fr-fr', 'fr_fr', 'frFR', 'FR_FR', 'FR-FR', 'FR'].forEach(push);
    } else if (lower.startsWith('en')) {
      ['en_US', 'en-GB', 'en-us', 'en_gb', 'enGB', 'enUS', 'EN_US', 'EN-GB', 'EN'].forEach(push);
    }

    return Array.from(candidates).filter(Boolean);
  };

  const isDisplayableString = (value: any) => {
    if (typeof value !== 'string') return false;
    const trimmed = value.trim();
    if (!trimmed) return false;
    const lower = trimmed.toLowerCase();
    return lower !== '---' && lower !== 'null' && lower !== 'undefined';
  };

  const resolveLocalizedEntry = (source: any, lang: string) => {
    if (!source) return '';
    for (const key of buildLanguageCandidates(lang)) {
      const candidate = source?.[key];
      if (isDisplayableString(candidate)) {
        return candidate;
      }
    }
    return '';
  };

  nuxtApp.provide('slugify', slugify);
  nuxtApp.provide('isUserFromUS', isUserFromUS);
  nuxtApp.provide('convertDimensions', convertDimensions);
  nuxtApp.provide('convertWeight', convertWeight);
  nuxtApp.provide('nl2br', nl2br);
  nuxtApp.provide('nl2brCommas', nl2brCommas);

  // You can alternatively use this format, which comes with automatic type support
  return {

    provide: {

      getRightsMatrix(status: string, action: string) {
        if (!status || !action) return 0;

        // Map labels to keys if necessary
        const statusMap: Record<string, string> = {
          'Fabrication': 'CLOSED',
          'Clôturé': 'ENDED',
          'Draft': 'DRAFT',
          'To Review': 'TO REVIEW',
          'Quote': 'QUOTE',
          'Order Form': 'ORDER FORM'
        };
        const statusKey = statusMap[status] || status;

        const userRole = globalStore.user?.role;
        if (!userRole) {
          console.warn(`getRightsMatrix: No user role defined`);
          return 0;
        }

        // Utiliser la matrice de l'API (source unique de vérité)
        const matrix = globalStore.rightsMatrix;

        if (!matrix || !matrix.roles) {
          console.warn(`getRightsMatrix: Rights matrix not loaded from API`);
          return 0;
        }

        if (!matrix.roles[userRole]) {
          console.warn(`getRightsMatrix: Invalid user role '${userRole}'`);
          return 0;
        }

        const roleRights = matrix.roles[userRole];
        if (!roleRights.quotations || !roleRights.quotations[status]) {
          console.warn(`getRightsMatrix: Invalid status '${status}' for role '${userRole}'`);
          return 0;
        }

        const value = roleRights.quotations[status][action as keyof typeof roleRights.quotations[typeof status]];
        // Si l'action n'existe pas dans la matrice, c'est N/A (non applicable) → retourner 0 silencieusement
        if (typeof value === 'undefined') {
          return 0;
        }

        return value;
      },

      /**
       * Vérifie si l'utilisateur peut effectuer une action CRUD sur une ressource
       * @param resource - La ressource (users, stores, countries, etc.)
       * @param action - L'action (view_bo, view_front, create, edit, delete)
       * @returns boolean
       */
      canPerformAction(resource: string, action: string): boolean {
        const userRole = globalStore.user?.role;
        if (!userRole) return false;

        const matrix = globalStore.rightsMatrix;
        if (!matrix || !matrix.roles || !matrix.roles[userRole]) {
          return false;
        }

        const roleRights = matrix.roles[userRole];
        const resourceRights = roleRights[resource as keyof typeof roleRights];

        if (!resourceRights || typeof resourceRights !== 'object') {
          return false;
        }

        const actionRights = (resourceRights as any)[action];
        return actionRights?.allowed === 1;
      },

      /**
       * Vérifie si l'utilisateur peut voir une ressource en front (view_bo OU view_front)
       * Utile pour les sélecteurs, dropdowns et composants front qui ont besoin de données
       * même si l'utilisateur n'a pas accès au BO de cette ressource.
       * @param resource - La ressource (users, stores, countries, etc.)
       * @returns boolean
       */
      canViewFront(resource: string): boolean {
        return this.canPerformAction(resource, 'view_bo') || this.canPerformAction(resource, 'view_front');
      },

      /**
       * Récupère le scope d'une action pour l'utilisateur courant
       * @param resource - La ressource (users, stores, etc.)
       * @param action - L'action (view, create, edit, delete)
       * @returns string | undefined
       */
      getActionScope(resource: string, action: string): string | undefined {
        const userRole = globalStore.user?.role;
        if (!userRole) return undefined;

        const matrix = globalStore.rightsMatrix;
        if (!matrix || !matrix.roles || !matrix.roles[userRole]) {
          return undefined;
        }

        const roleRights = matrix.roles[userRole];
        const resourceRights = roleRights[resource as keyof typeof roleRights];

        if (!resourceRights || typeof resourceRights !== 'object') {
          return undefined;
        }

        const actionRights = (resourceRights as any)[action];
        return actionRights?.scope;
      },

      /**
       * Récupère la hiérarchie des rôles que l'utilisateur peut gérer
       * @returns string[]
       */
      getRoleHierarchy(): string[] {
        const userRole = globalStore.user?.role;
        if (!userRole) return [];

        const matrix = globalStore.rightsMatrix;
        if (!matrix || !matrix.roles || !matrix.roles[userRole]) {
          return [];
        }

        return matrix.roles[userRole].role_hierarchy || [];
      },

      getStatusColor(status: string) {
        // Note: CLOSED = "Fabrication" (label), ENDED = "Clôturé" (label)
        switch (status) {
          case "DRAFT":
            return "#5253FF";
          case "TO REVIEW":
            return "#4EE25E";
          case "QUOTE":
            return "#0A7E00";
          case "ORDER FORM":
            return "#F05818";
          case "CLOSED":        // Affiché comme "Fabrication"
            return "#2A2A2A";
          case "ENDED":         // Affiché comme "Clôturé"
            return "#723813";
          case "CANCELLED":
            return "#393939";
          default:
            return "#E0E0E0";
        }
      },

      capitalize(string: string) {
        return string[0].toUpperCase() + string.slice(1).toLowerCase()
      },

      formatPrice(currency_code: string, price: number | string) {
        // ISO 4217: 3 letters only; guard against bad data (e.g. "USDdfs")
        const raw = (currency_code || '').trim().toUpperCase();
        const validCurrency = /^[A-Z]{3}$/.test(raw) ? raw : 'EUR';

        // Déterminer une locale sûre: pays de l'utilisateur -> langue appli -> fallback navigateur/EN
        let locale = 'en-US';
        try {
          const langFallback = (globalStore.language === 'en') ? 'en-US' : 'fr-FR';
          const userCountryId = Number(globalStore.user?.country_id);
          const userCountry = globalStore.countries?.data?.find(it => it.id == userCountryId);
          locale = userCountry?.locale || langFallback || locale;
        } catch {
          // garder la valeur par défaut
        }

        const priceFormatter = new Intl.NumberFormat(locale, { style: 'currency', currency: validCurrency });
        const numeric = typeof price === 'string' ? Number(price) : price;
        if (typeof numeric !== 'number' || Number.isNaN(numeric)) return '';
        // Keep amount + currency on one line in PDF/table cells (no wrap before £ / € / code)
        return priceFormatter
          .format(numeric)
          .replace(/[\u202f\u2009\u00a0]/g, '\xa0')
          .replace(/(\d)\s+([^\d\s])/g, '$1\xa0$2')
          .replace(/([^\d\s])\s+(\d)/g, '$1\xa0$2');
      },

      discount(ttPrice: number, discount: number, vat: number, tax_exemption: boolean) {

        var price = tax_exemption ? ttPrice / (1 + vat) : ttPrice
        return price - price * (1 - discount / 100)
      },

      isUserRoleGreaterThan(role: string) {

        if (!globalStore || !globalStore.user || !globalStore.user.role) {
          return false
        }

        const user_role = globalStore.user.role

        if (role == 'sales_associate_front') {
          return user_role == "admin"
            || user_role == "regional_manager"
            || user_role == "store_manager"
            || user_role == "sales_associate_back"
            || user_role == "sales_associate_front"
        }
        if (role == 'sales_associate_back') {
          return user_role == "admin"
            || user_role == "regional_manager"
            || user_role == "store_manager"
            || user_role == "sales_associate_back"
        }
        if (role == 'store_manager') {
          return user_role == "admin"
            || user_role == "regional_manager"
            || user_role == "store_manager"
        }
        if (role == 'regional_manager') {
          return user_role == "admin"
            || user_role == "regional_manager"
        }
        if (role == 'admin') {
          return user_role == "admin"
        }
        return false
      },

      getNextStatus(status: string) {
        // Note: CLOSED = "Fabrication" (label), ENDED = "Clôturé" (label)
        switch (status) {
          case "DRAFT":
            return "TO REVIEW"
          case "TO REVIEW":
            return "QUOTE"
          case "QUOTE":
            return "ORDER FORM"
          case "ORDER FORM":
            return "CLOSED"     // CLOSED = "Fabrication"
          case "CLOSED":
            return "ENDED"      // ENDED = "Clôturé"
        }
        return null  // Pas de statut suivant (ENDED, CANCELLED)
      },

      canEdit: (status: string) => {
        // Utiliser la matrice de droits pour déterminer si l'utilisateur peut éditer
        const editRight = nuxtApp.$getRightsMatrix(status, 'edit');
        return editRight === 1;
      },

      getParameter(key: string, lang: string = "") {
        // Gérer les valeurs null ou undefined
        if (!key || key === null || key === undefined) return ''
        lang = lang.length ? lang : globalStore.language
        const labels = (globalStore.parameters && globalStore.parameters.labels) ? globalStore.parameters.labels : {}
        let label = labels[key]
        if (!label) {
          const lower = key.toLowerCase()
          const matchedKey = Object.keys(labels).find(k => k.toLowerCase() === lower)
          if (matchedKey) {
            label = labels[matchedKey]
          }
        }
        let result = label ? (
          label[lang] ? label[lang] :
            (label["en"] ? label["en"] : key)
        ) : key

        // Transform "Orfévrerie" → "Puiforcat" in front pages (not admin)
        if (typeof result === 'string') {
          try {
            const route = nuxtApp.$router?.currentRoute?.value;
            const isAdminPage = route?.path?.startsWith('/admin');

            if (!isAdminPage) {
              // Replace variations of "orfévrerie" with "Puiforcat"
              result = result
                .replace(/Orfèvrerie/g, 'Puiforcat')
                .replace(/orfèvrerie/g, 'Puiforcat')
                .replace(/Orfevrerie/g, 'Puiforcat')
                .replace(/orfevrerie/g, 'Puiforcat')
                .replace(/ORFÈVRERIE/g, 'PUIFORCAT')
                .replace(/ORFEVRERIE/g, 'PUIFORCAT');
            }
          } catch (e) {
            // If route is not available, do nothing
          }
        }

        return result
      },

      capitalizeFirst(text) {
        if (!text) return '';
        return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
      },

      // Helper to display metier type (for front pages only, admin keeps original)
      displayMetierLabel(metier: string): string {
        try {
          const route = nuxtApp.$router?.currentRoute?.value;
          const isAdminPage = route?.path?.startsWith('/admin');

          if (isAdminPage) {
            // Admin: keep original labels
            if (metier === 'mobilier') return 'Mobilier';
            if (metier === 'porcelaine' || metier === 'lth') return 'Porcelaine';
            if (metier === 'orfevrerie') return 'Orfèvrerie';
          } else {
            // Front: transform orfevrerie → Puiforcat
            if (metier === 'mobilier') return 'Mobilier';
            if (metier === 'porcelaine' || metier === 'lth') return 'Porcelaine';
            if (metier === 'orfevrerie') return 'Puiforcat';
          }
        } catch (e) {
          // Fallback if route not available: assume front page
          if (metier === 'mobilier') return 'Mobilier';
          if (metier === 'porcelaine' || metier === 'lth') return 'Porcelaine';
          if (metier === 'orfevrerie') return 'Puiforcat';
        }

        return metier || '';
      },

      getTranslation: (input: any) => {
        let obj = null
        if (typeof input == "string") {
          try {
            obj = JSON.parse(input);
          } catch (e) {
            return input;
          }
        } else {
          obj = input
        }
        return obj ? (
          obj[globalStore.language] ? obj[globalStore.language] :
            (obj["en"] ? obj["en"] : "---")
        ) : "---"
      },

      // Strict: only current language (with locale variants), treat plain string as FR-only
      getTranslationStrict: (input: any) => {
        const lang = globalStore.language || 'fr';
        if (!input) return '';

        if (typeof input === 'object') {
          return resolveLocalizedEntry(input, lang);
        }

        if (typeof input === 'string') {
          try {
            const obj = JSON.parse(input);
            const resolved = resolveLocalizedEntry(obj, lang);
            if (resolved) return resolved;
          } catch (e) {
            // Plain string → FR-only content
            if (lang.toLowerCase().startsWith('fr') && isDisplayableString(input)) {
              return input;
            }
          }
        }

        return '';
      },

      getInCurrency: (currency_code: string, input: any) => {
        let obj = null
        if (typeof input == "string") {
          try {
            obj = JSON.parse(input);
          } catch (e) {
            return "---";
          }
        } else {
          obj = input
        }

        return (obj && obj[currency_code]) ? obj[currency_code] : "---"
      },

      // DRY helper: checks if a translatable field has displayable content
      hasDisplayableTranslation: (input: any) => {
        try {
          const translated = (nuxtApp as any).$getTranslation ? (nuxtApp as any).$getTranslation(input) : '';
          if (typeof translated !== 'string') return false;
          const t = translated.trim();
          if (!t || t === '---') return false;
          const lower = t.toLowerCase();
          return lower !== 'null' && lower !== 'undefined';
        } catch (e) {
          if (typeof input === 'string') {
            const t = input.trim();
            return t !== '' && t !== '---' && t.toLowerCase() !== 'null' && t.toLowerCase() !== 'undefined';
          }
          if (input && typeof input === 'object') {
            const t = (input.fr || input.en || '').toString().trim();
            return t !== '' && t !== '---' && t.toLowerCase() !== 'null' && t.toLowerCase() !== 'undefined';
          }
          return false;
        }
      },

      // Strict variant: only current language has to be present; no cross-language fallbacks
      hasDisplayableTranslationStrict: (input: any) => {
        try {
          const translated = (nuxtApp as any).$getTranslationStrict ? (nuxtApp as any).$getTranslationStrict(input) : '';
          if (typeof translated !== 'string') return false;
          const t = translated.trim();
          if (!t) return false;
          const lower = t.toLowerCase();
          return lower !== '---' && lower !== 'null' && lower !== 'undefined';
        } catch (e) {
          return false;
        }
      },

      // Unified helper for accordions: returns both visibility and the strict-translated text
      accordionContent: (input: any) => {
        const text = (nuxtApp as any).$getTranslationStrict ? (nuxtApp as any).$getTranslationStrict(input) : '';
        if (typeof text !== 'string') return { show: false, text: '' };
        let t = text.trim();
        if (!t) return { show: false, text: '' };
        // Strip "ID: " prefix if present (for careInstructions)
        if (t.startsWith('ID: ')) {
          t = t.substring(4);
        }
        const lower = t.toLowerCase();
        const show = lower !== '---' && lower !== 'null' && lower !== 'undefined';
        return { show, text: show ? t : '' };
      },

      formatIsoDate(isoString: string) {
        const date = new Date(isoString);
        const options: Intl.DateTimeFormatOptions = { year: 'numeric', month: 'numeric', day: 'numeric' };
        const locale = globalStore.language === "en" ? "en-EN" : "fr-FR";

        return date.toLocaleDateString(locale, options);
      },

      snackbar: (type: String, text: String) => {
        return {
          display: true, text: text, color: type, icon: "$" + type
        }

      },

      get: (route: string) => {
        return useFetch(route, {
          method: "GET",
          baseURL: globalStore.apiBase,
          headers: { Authorization: "Bearer " + tokenStore.currentValue }
        });
      },

      post: async (route: string, body: any) => {
        try {
          const token = tokenStore.currentValue;
          const data = await $fetch(route, {
            method: "POST",
            baseURL: globalStore.apiBase,
            headers: { Authorization: "Bearer " + token },
            body: body
          });
          return { data: ref(data), error: ref(null) };
        } catch (err: any) {
          return { data: ref(null), error: ref(err) };
        }
      },

      patch: async (route: string, id: string, body: any) => {
        try {
          const data = await $fetch(route + "/" + id, {
            method: "PATCH",
            baseURL: globalStore.apiBase,
            headers: { Authorization: "Bearer " + tokenStore.currentValue },
            body: body
          });
          return { data: ref(data), error: ref(null) };
        } catch (err: any) {
          return { data: ref(null), error: ref(err) };
        }
      },

      delete: async (route: string, id: string) => {
        try {
          const data = await $fetch(route + "/" + id, {
            method: "DELETE",
            baseURL: globalStore.apiBase,
            headers: { Authorization: "Bearer " + tokenStore.currentValue }
          });
          return { data: ref(data), error: ref(null) };
        } catch (err: any) {
          return { data: ref(null), error: ref(err) };
        }
      },

      hasValidInfo(info) {
        if (!info) return false;
        if (typeof info === 'string') {
          const trimmed = info.trim().toLowerCase();
          return trimmed !== '' && trimmed !== 'null' && trimmed !== 'undefined';
        }
        if (typeof info === 'object') {
          return Object.values(info).some(val => !!val && val.trim && val.trim().toLowerCase() !== 'null' && val.trim().toLowerCase() !== 'undefined');
        }
        return true;
      },
    }
  }
})
